import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { MapPin, Flag, Car, BellRing, CheckCircle2 } from "lucide-react";
import { usePrefs } from "@/lib/context";
import { LANGS } from "@/lib/i18n";
import { fetcher } from "@/lib/hooks";
import { StatusBadge } from "@/components/common";
import { LiveMap, EtaLine, isLive, toMapItem, etaFresh } from "@/components/LiveMap";
import { ReviewForm, Stars } from "@/components/Reviews";

const beep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); o.frequency.value = 880; o.connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.35);
  } catch (_) {}
};

async function alertGuest(title, body) {
  navigator.vibrate?.([200, 100, 200]);
  beep();
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const reg = await navigator.serviceWorker?.getRegistration();
  if (reg) reg.showNotification(title, { body, icon: "/icon-192.png", tag: "gst-arrival" });
  else new Notification(title, { body });
}

function ArrivalAlert({ data, token }) {
  const { t } = usePrefs();
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const near = etaFresh(data.eta) && data.eta.seconds <= 300;
  const m = near ? Math.max(1, Math.round(data.eta.seconds / 60)) : 0;
  const text = near ? t(data.eta.target === "pickup" ? "arrivingPickup" : "arrivingDest").replace("{m}", m) : "";
  useEffect(() => {
    if (!near) return;
    const key = `gst_alert_${token}_${data.eta.target}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    alertGuest(`${data.number} · Gestimmo`, text);
  }, [near, token, data.eta?.target]); // eslint-disable-line react-hooks/exhaustive-deps
  const ask = async () => {
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === "granted" && near) alertGuest(`${data.number} · Gestimmo`, text);
  };
  return (
    <>
      {near && <div className="rise flex items-center gap-3 rounded-sm bg-[#0F172A] p-4 text-white" data-testid="arrival-banner"><BellRing size={20} className="shrink-0 text-[#E5C46B]" /><span className="font-semibold">{text}</span></div>}
      {perm === "default" && <button onClick={ask} className="flex w-full items-center justify-center gap-2 rounded-sm border border-[#B8860B] bg-white py-2.5 text-sm font-semibold text-[#996515] hover:bg-[#fdf8ec]" data-testid="arrival-notify-button"><BellRing size={15} />{t("notifyMe")}</button>}
      {perm === "granted" && <p className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="arrival-notify-on"><BellRing size={13} />{t("notifyOn")}</p>}
    </>
  );
}

export const fillThanks = (tpl, v) => tpl.replace(/\{guest\}/g, v.guest).replace(/\{driver\}/g, v.driver).replace(/\{rating\}/g, v.rating);

function thankYou(data, t, lang) {
  const r = data.review;
  const custom = data.thank_you?.[lang];
  const v = { guest: r.guest_name || t("guest"), driver: data.driver || t("yourDriver"), rating: r.rating };
  if (custom) return fillThanks(custom, v);
  return fillThanks(t(r.rating <= 2 ? (r.guest_name ? "thanksLowNamed" : "thanksLow") : r.guest_name ? "thanksAutoNamed" : "thanksAuto"), v);
}

function Ended({ data, token }) {
  const { t, lang, fdatetime } = usePrefs();
  const qc = useQueryClient();
  return (
    <div className="rise space-y-4 rounded-sm border border-slate-200 bg-white p-6" data-testid="public-track-ended">
      <div className="flex items-center gap-2 font-display text-2xl font-bold"><CheckCircle2 size={22} className="text-emerald-600" />{t("tripEnded")} · {data.number}</div>
      <div className="text-sm text-slate-600">{data.pickup} → {data.destination}{data.driver ? ` · ${t("driver")} : ${data.driver}` : ""}</div>
      {data.review
        ? <div className="space-y-2 rounded-sm bg-[#fdf8ec] p-4" data-testid="review-thanks"><div className="font-display text-lg font-bold leading-snug" data-testid="review-thanks-message">{thankYou(data, t, lang)}</div><Stars value={data.review.rating} size={18} testId="review-done-stars" />{data.review.comment && <p className="text-sm text-slate-700">« {data.review.comment} »</p>}</div>
        : <><ReviewForm token={token} onDone={() => qc.invalidateQueries({ queryKey: ["public-track", token] })} /><p className="text-[11px] text-slate-400">{t("reviewUntil")} {fdatetime(data.review_until)}</p></>}
    </div>
  );
}

export default function PublicTrack() {
  const { token } = useParams();
  const { t, lang, setLang, fdatetime } = usePrefs();
  const { data, error, isLoading } = useQuery({ queryKey: ["public-track", token], queryFn: () => fetcher(`/public/track/${token}`), refetchInterval: 8000, retry: false });
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="public-track-page">
      <header className="flex items-center justify-between bg-[#0F172A] px-5 py-4 text-white">
        <div><div className="font-display text-lg font-bold tracking-[0.12em]">GESTIMMO</div><div className="text-[10px] uppercase tracking-[0.2em] text-[#E5C46B]">{t("trackingFor")}</div></div>
        <div className="flex gap-1 text-[11px] font-bold">{LANGS.map(([l]) => <button key={l} onClick={() => setLang(l)} data-testid={`public-lang-${l}`} className={`px-1.5 uppercase ${lang === l ? "text-[#E5C46B]" : "text-slate-400"}`}>{l}</button>)}</div>
      </header>
      <main className="mx-auto max-w-3xl space-y-4 p-4 sm:p-6">
        {isLoading && <p className="text-sm text-slate-500">…</p>}
        {error && <div className="rounded-sm border border-slate-200 bg-white p-8 text-center text-sm text-slate-600" data-testid="public-track-expired">{t("linkExpired")}</div>}
        {data?.ended && <Ended data={data} token={token} />}
        {data && !data.ended && <>
          <ArrivalAlert data={data} token={token} />
          <div className="rise rounded-sm border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 font-display text-2xl font-bold"><Car size={20} className="text-[#996515]" />{data.number}</span>
              <StatusBadge value={data.status} />
            </div>
            <div className="mt-3 grid gap-1 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><MapPin size={13} />{data.pickup || "—"}</span>
              <span className="flex items-center gap-1.5"><Flag size={13} />{data.destination || "—"}</span>
              <span className="text-xs text-slate-500">{t("scheduledAt")} : {fdatetime(data.scheduled_at)}{data.driver ? ` · ${t("driver")} : ${data.driver}` : ""}</span>
            </div>
            <div className="mt-3 flex items-center justify-between gap-2">
              <EtaLine eta={data.eta} testId="public-eta" />
              {data.location ? <span className={`flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] font-bold uppercase ${isLive(data.location) ? "text-emerald-700" : "text-slate-400"}`} data-testid="public-live-status">{isLive(data.location) && <span className="live-dot" />}{isLive(data.location) ? t("enRoute") : `${t("lastUpdate")} ${fdatetime(data.location.at)}`}</span>
                : <span className="text-[11px] text-slate-400">{t("waitingPosition")}</span>}
            </div>
          </div>
          <LiveMap follow height="h-[60vh]" testId="public-track-map" transports={[toMapItem(data, `${data.number} · ${data.driver || ""}`)]} />
        </>}
        <p className="pt-4 text-center text-[11px] italic text-slate-400">Conçue par ELOUNGOU ÉTIENNE LANDRY</p>
      </main>
    </div>
  );
}
